import {
  BarChartOutlined,
  BranchesOutlined,
  GithubOutlined,
  LineChartOutlined,
  PieChartOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { Button, Input, Select } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { demoApps, DemoQlikApp } from './demoData';
import { CachedSyncSummary, readSyncSummary, syncSummaryLabel } from './syncSummary';
import { clickCiApiUrl } from './apiUrl';

const { Option } = Select;

const AppGlyph: React.FC<{ type: DemoQlikApp['icon'] }> = ({ type }) => {
  const glyphs = {
    bars: <BarChartOutlined />,
    pie: <PieChartOutlined />,
    line: <LineChartOutlined />,
    cart: <ShoppingCartOutlined />,
  };
  return <span className={`app-glyph app-glyph-${type}`}>{glyphs[type]}</span>;
};

type AppRow = DemoQlikApp & { syncSummary?: CachedSyncSummary };

type NextStepVariant = 'primary' | 'warning' | 'neutral' | 'connect';

const nextStepFor = (app: AppRow): { label: string; path: string; variant: NextStepVariant } => {
  if (app.gitStatus === 'not-connected') {
    return { label: 'Connect Git', path: `/apps/${app.id}`, variant: 'connect' };
  }
  if (app.syncSummary?.status === 'conflict') {
    return { label: 'Resolve conflict', path: `/apps/${app.id}/commit`, variant: 'warning' };
  }
  if (app.syncSummary?.status === 'git_ahead') {
    return { label: 'Review & pull', path: `/apps/${app.id}`, variant: 'primary' };
  }
  if (app.syncSummary && ['qlik_ahead', 'auto_merge'].includes(app.syncSummary.status)) {
    return { label: 'Review & commit', path: `/apps/${app.id}/commit`, variant: 'primary' };
  }
  if (/behind|git ahead|git has .*ready to pull|ready to pull/i.test(app.syncLabel)) {
    return { label: 'Review & pull', path: `/apps/${app.id}`, variant: 'primary' };
  }
  if (/conflict|change|qlik ahead|qlik has .*ready to push|both have updates|review required/i.test(app.syncLabel)) {
    return { label: 'Review & commit', path: `/apps/${app.id}/commit`, variant: 'primary' };
  }
  if (/comparison required/i.test(app.syncLabel)) {
    return { label: 'Compare with Git', path: `/apps/${app.id}/commit`, variant: 'primary' };
  }
  if (/comparison failed/i.test(app.syncLabel)) {
    return { label: 'Review issue', path: `/apps/${app.id}/commit`, variant: 'warning' };
  }
  return { label: 'Open overview', path: `/apps/${app.id}`, variant: 'neutral' };
};

const liveSyncLabelFor = (summary: any) => syncSummaryLabel(summary);

const reviewSignalFor = (app: AppRow): 'action' | 'conflict' | undefined => {
  if (app.syncSummary?.status === 'conflict' || /conflict/i.test(app.syncLabel)) return 'conflict';
  if (app.syncSummary && ['git_ahead', 'qlik_ahead', 'auto_merge'].includes(app.syncSummary.status)) return 'action';
  if (/ready to pull|ready to push|both have updates|review required|change/i.test(app.syncLabel)) return 'action';
  return undefined;
};

const stateCopyFor = (app: AppRow) => {
  const summary = app.syncSummary;
  if (app.gitStatus === 'not-connected') return 'Not in GitHub';
  if (summary?.status === 'conflict' || /conflict/i.test(app.syncLabel)) return 'Action required';
  if (summary && ['git_ahead', 'qlik_ahead', 'auto_merge'].includes(summary.status)) return 'Updates available';
  if (/ready to pull|ready to push|both have updates|review required|change/i.test(app.syncLabel)) return 'Updates available';
  if (summary?.status === 'in_sync' || /up to date/i.test(app.syncLabel)) return 'Up to date';
  if (/comparison failed/i.test(app.syncLabel)) return 'Comparison failed';
  if (/checking/i.test(app.syncLabel)) return 'Checking';
  return app.syncLabel;
};

export const QlikAppsPage: React.FC = () => {
  const [apps, setApps] = useState<AppRow[]>(() => demoApps.map(app => {
    const cached = readSyncSummary(app.id);
    return cached ? { ...app, syncSummary: cached, syncLabel: liveSyncLabelFor(cached), lastSync: new Date(cached.comparedAt).toLocaleString() } : app;
  }));
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const refreshTimer = useRef<number>();
  const mounted = useRef(true);

  const refreshApps = useCallback(async () => {
    if (typeof fetch !== 'function') return;
    try {
      const response = await fetch(`${clickCiApiUrl}/api/demo-ci/apps`);
      if (!response.ok) return;
      const payload = await response.json();
      const liveApps = payload?.result?.apps;
      if (!Array.isArray(liveApps)) return;
      setApps(current => current.map(app => {
        const liveApp = liveApps.find((candidate: { id?: string }) => candidate.id === app.id);
        if (!liveApp) return app;
        const summary = liveApp.syncSummary;
        const summaryLabel = summary?.error
          ? 'Comparison failed - open Commit to retry'
          : summary?.status === 'checking'
            ? 'Checking Qlik and Git for changes...'
            : summary?.status
              ? liveSyncLabelFor(summary)
              : app.syncLabel;
        return {
          ...app,
          name: liveApp.name ?? app.name,
          repository: liveApp.mapping?.repository ?? app.repository,
          branch: liveApp.mapping?.branch ?? app.branch,
          lastSync: summary?.comparedAt ? new Date(summary.comparedAt).toLocaleString() : app.lastSync,
          syncLabel: summaryLabel,
          syncSummary: summary?.status && summary.status !== 'checking' && !summary.error ? summary as CachedSyncSummary : app.syncSummary,
        };
      }));
    } catch {
      // Keep cached statuses visible while the optional local BFF is offline.
    } finally {
      window.clearTimeout(refreshTimer.current);
      if (mounted.current) refreshTimer.current = window.setTimeout(() => { void refreshApps(); }, 5000);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refreshApps();
    return () => {
      mounted.current = false;
      window.clearTimeout(refreshTimer.current);
    };
  }, [refreshApps]);

  const visibleApps = useMemo(() => apps.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = status === 'all' || app.gitStatus === status;
    return matchesSearch && matchesStatus;
  }), [apps, search, status]);

  return (
    <section className="apps-page">
      <div className="page-heading">
        <h1>Qlik Apps</h1>
        <p>See the latest known Qlik-to-Git state, then open the controlled workflow for review, commit, or Git updates.</p>
      </div>

      <div className="apps-toolbar">
        <Input aria-label="Search apps" className="apps-search" placeholder="Search apps by name..." prefix={<SearchOutlined />} value={search} onChange={event => setSearch(event.target.value)} />
        <Select aria-label="Git status" value={status} onChange={setStatus}>
          <Option value="all">Git status</Option>
          <Option value="connected">Connected</Option>
          <Option value="not-connected">Not connected</Option>
        </Select>
        <Select aria-label="Repository" defaultValue="all"><Option value="all">Repository</Option></Select>
        <Select aria-label="Branch" defaultValue="all"><Option value="all">Branch</Option></Select>
        <Button aria-label="Refresh app status" icon={<ReloadOutlined />} onClick={() => void refreshApps()} />
      </div>

      <div className="apps-table" role="table" aria-label="Qlik Apps">
        <div className="apps-table-head" role="row">
          <span>App name</span><span>Latest known state</span><span>Repository / Branch</span><span>Last comparison</span><span>Next step</span>
        </div>
        {visibleApps.map(app => {
          const nextStep = nextStepFor(app);
          const reviewSignal = reviewSignalFor(app);
          return (
            <div className="apps-row" role="row" key={app.id}>
              <div className="app-cell">
                <AppGlyph type={app.icon} />
                <div>
                  <Link className={`app-name ${reviewSignal ? `has-${reviewSignal}-signal` : ''}`} to={`/apps/${app.id}`}>
                    {app.name}
                    {reviewSignal && <span className={`app-review-signal is-${reviewSignal}`} aria-hidden="true" />}
                  </Link>
                  <div className="app-secondary-line"><span className="muted">{app.fileName}</span></div>
                </div>
              </div>
              <div>
                <div className={`git-state ${app.gitStatus}`}>
                  <span className="status-dot" /> {app.gitStatus === 'connected' ? 'Connected' : 'Not connected'}
                </div>
                <div className="muted">{stateCopyFor(app)}</div>
              </div>
              <div className="repo-cell">
                {app.repository ? <><div><GithubOutlined /> {app.repository}</div><div><BranchesOutlined /> {app.branch}</div></> : <><div>-</div><div>-</div></>}
              </div>
              <div>{app.lastSync ? <><div>{app.lastSync}</div><div className="muted">by {app.lastSyncBy}</div></> : '-'}</div>
              <div className="row-actions"><Link className={`ant-btn next-step-button is-${nextStep.variant}`} aria-label={`${nextStep.label} for ${app.name}`} to={nextStep.path}>{nextStep.label}</Link></div>
            </div>
          );
        })}
      </div>
      <p className="apps-status-note">Status answers the two user questions: does Qlik have updates to push, or does Git have updates to pull? Commit still validates the latest evidence before writing.</p>
    </section>
  );
};
